import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';

// Enable Map and Set support in Immer
enableMapSet();

import { GalaxyStore } from './types';
import { createNetworkSlice } from './slices/networkSlice';
import { createBlockchainSlice } from './slices/blockchainSlice';
import { createMempoolSlice } from './slices/mempoolSlice';
import { createSceneSlice } from './slices/sceneSlice';
import { createUISlice } from './slices/uiSlice';
import { eventBus, TransactionEvent, BlockEvent, StatsUpdateEvent } from '../state/EventBus';
import { logger } from '../utils/Logger';

// Main store creation
export const useGalaxyStore = create<GalaxyStore>()(
  devtools(
    persist(
      subscribeWithSelector(
        immer((...args) => ({
          // Initial nested state
          network: {
            isConnected: false,
            currentHeight: 0,
            hashrate: 0,
            difficulty: 0,
            connectionStatus: 'disconnected',
            nextAdjustment: {
              blocksRemaining: 0,
              estimatedTime: 0,
              estimatedChange: 0,
            },
            lastUpdate: Date.now(),
          },
          blockchain: {
            blocks: new Map(),
            transactions: new Map(),
          },
          mempool: {
            size: 0,
            count: 0,
            feeHistogram: [],
            avgFeeRate: 0,
            congestionLevel: 'low' as const,
            lastUpdate: Date.now(),
          },
          scene: {
            activeBitcoinSun: null,
            activeDysonSphere: null,
            activeMempoolNebula: null,
            activeShips: new Set(),
            activeBlockPlanets: new Set(),
            interactions: {
              selectedObjectId: null,
              hoveredObjectId: null,
              cameraMode: 'follow',
              showTrajectories: true,
            },
            performance: {
              lastFPS: 60,
              avgFPS: 60,
              memoryUsage: 0,
              activeBodies: 0,
              frameTime: 16,
              qualityLevel: 'high',
            },
            dysonSphereProgress: 0,
            planetPositions: new Map(),
          },
          ui: {
            selectedObject: null,
            hoveredObject: null,
            cameraMode: 'orbit' as const,
            timeScale: 1,
            filters: {
              showPendingOnly: false,
              showConfirmedOnly: false,
            },
            performance: {
              fps: 60,
              frameTime: 16.67,
              memoryUsage: 0,
              activeObjects: 0,
            },
            settings: {
              hudOpacity: 1,
              showKeyboardHelp: false,
            },
            interaction: {
              inputMode: 'mouse' as const,
              cursorState: 'default' as const,
              isInteractionEnabled: true,
            },
          },
          
          // Add all actions from slices
          ...createNetworkSlice(...args),
          ...createBlockchainSlice(...args),
          ...createMempoolSlice(...args),
          ...createSceneSlice(...args),
          ...createUISlice(...args),
        }))
      ),
      {
        name: 'bitcoin-galaxy-storage',
        // Only persist UI settings
        partialize: (state) => ({
          ui: {
            cameraMode: state.ui.cameraMode,
            timeScale: state.ui.timeScale,
            filters: state.ui.filters,
          },
        }),
      }
    ),
    {
      name: 'BitcoinGalaxy',
    }
  )
);

// Connect EventBus to store
logger.debug('Setting up event listeners', 'GalaxyStore');

eventBus.on<TransactionEvent>('transaction', (event) => {
  logger.debug(`Transaction ${event.data.id} (${event.data.amount} sats)`, 'GalaxyStore');
  useGalaxyStore.getState().addTransaction(event.data);
});

eventBus.on<BlockEvent>('block', (event) => {
  logger.info(`Block ${event.data.height} (${event.data.transactionCount} txs)`, 'GalaxyStore');
  useGalaxyStore.getState().addBlock(event.data);
});

logger.debug('Event listeners registered', 'GalaxyStore');

eventBus.on<StatsUpdateEvent>('stats_update', (event) => {
  const { updateNetworkStats, updateMempool } = useGalaxyStore.getState();
  
  updateNetworkStats(event.data);
  
  if (event.data.mempool) {
    updateMempool({
      size: event.data.mempool.size,
      count: event.data.mempool.txCount,
    });
  }
});

// Computed values hook
export const useComputedValues = () => {
  const transactions = useGalaxyStore((state) => state.blockchain.transactions);
  const blocks = useGalaxyStore((state) => state.blockchain.blocks);
  const activeObjects = useGalaxyStore((state) => state.scene.activeShips.size);
  
  // Calculate average fee rate
  const transactionArray = Array.from(transactions.values());
  const averageFeeRate = transactionArray.length > 0
    ? transactionArray.reduce((sum, tx) => sum + tx.feeRate, 0) / transactionArray.length
    : 0;
  
  // Calculate transaction velocity (transactions per second)
  const recentTransactions = transactionArray.filter(
    tx => Date.now() - tx.spawnedAt < 60000 // Last minute
  );
  const transactionVelocity = recentTransactions.length / 60;
  
  // Calculate average block time
  const blockArray = Array.from(blocks.values()).sort((a, b) => b.height - a.height);
  let averageBlockTime = 600; // Default 10 minutes
  
  if (blockArray.length >= 2) {
    const timeDiffs = [];
    for (let i = 1; i < blockArray.length; i++) {
      timeDiffs.push(blockArray[i - 1].timestamp - blockArray[i].timestamp);
    }
    averageBlockTime = timeDiffs.reduce((sum, diff) => sum + diff, 0) / timeDiffs.length / 1000;
  }
  
  // Calculate total fees from last 10 blocks
  const totalFeesLast10Blocks = blockArray.reduce((sum, block) => sum + block.totalFees, 0);
  
  return {
    averageFeeRate,
    transactionVelocity,
    averageBlockTime,
    totalFeesLast10Blocks,
    activeObjectCount: activeObjects + blocks.size,
    frameRate: useGalaxyStore.getState().ui.performance.fps,
  };
};

// Selector hooks for performance
export const useNetwork = () => useGalaxyStore((state) => state.network);
export const useBlocks = () => useGalaxyStore((state) => state.blockchain.blocks);
export const useTransactions = () => useGalaxyStore((state) => state.blockchain.transactions);
export const useMempool = () => useGalaxyStore((state) => state.mempool);
export const useScene = () => useGalaxyStore((state) => state.scene);
export const useUI = () => useGalaxyStore((state) => state.ui);

export const useSelectedTransaction = () => {
  const selectedId = useGalaxyStore((state) => state.ui.selectedObject);
  const transactions = useGalaxyStore((state) => state.blockchain.transactions);
  
  if (!selectedId || !selectedId.startsWith('ship-')) return null;
  
  const txId = selectedId.replace('ship-', '');
  return transactions.get(txId) || null;
};

export const useSelectedBlock = () => {
  const selectedId = useGalaxyStore((state) => state.ui.selectedObject);
  const blocks = useGalaxyStore((state) => state.blockchain.blocks);
  
  if (!selectedId || !selectedId.startsWith('planet-')) return null;
  
  const height = parseInt(selectedId.replace('planet-', ''));
  return blocks.get(height) || null;
};
export const useSelectedMiningCollector = () => {
  const selectedId = useGalaxyStore((state) => state.ui.selectedObject);
  
  if (!selectedId || !selectedId.startsWith('collector-')) return null;
  
  // For mining collectors, we'll extract the data from the interaction system
  // since they're managed by the MiningOperationsManager, not stored in the main state
  return selectedId;
};

// Test helper
export const createTestStore = (initialState?: Partial<GalaxyStore>) => {
  return create<GalaxyStore>()(
    immer((...args) => ({
      // Initial nested state (same as main store)
      network: {
        isConnected: false,
        currentHeight: 0,
        hashrate: 0,
        difficulty: 0,
        connectionStatus: 'disconnected',
        nextAdjustment: {
          blocksRemaining: 0,
          estimatedTime: 0,
          estimatedChange: 0,
        },
        lastUpdate: Date.now(),
      },
      blockchain: {
        blocks: new Map(),
        transactions: new Map(),
      },
      mempool: {
        size: 0,
        count: 0,
        feeHistogram: [],
        avgFeeRate: 0,
        congestionLevel: 'low' as const,
        lastUpdate: Date.now(),
      },
      scene: {
        activeBitcoinSun: null,
        activeDysonSphere: null,
        activeMempoolNebula: null,
        activeShips: new Set(),
        activeBlockPlanets: new Set(),
        interactions: {
          selectedObjectId: null,
          hoveredObjectId: null,
          cameraMode: 'follow',
          showTrajectories: true,
        },
        performance: {
          lastFPS: 60,
          avgFPS: 60,
          memoryUsage: 0,
          activeBodies: 0,
          frameTime: 16,
          qualityLevel: 'high',
        },
        dysonSphereProgress: 0,
        planetPositions: new Map(),
      },
      ui: {
        selectedObject: null,
        hoveredObject: null,
        cameraMode: 'orbit' as const,
        timeScale: 1,
        filters: {
          showPendingOnly: false,
          showConfirmedOnly: false,
        },
        performance: {
          fps: 60,
          frameTime: 16.67,
          memoryUsage: 0,
          activeObjects: 0,
        },
        settings: {
          hudOpacity: 1,
          showKeyboardHelp: false,
        },
        interaction: {
          inputMode: 'mouse' as const,
          cursorState: 'default' as const,
          isInteractionEnabled: true,
        },
      },
      
      // Add all actions from slices
      ...createNetworkSlice(...args),
      ...createBlockchainSlice(...args),
      ...createMempoolSlice(...args),
      ...createSceneSlice(...args),
      ...createUISlice(...args),
      
      // Override with any initial state provided
      ...initialState,
    }))
  );
};